<template>
  <div class="rfr-visits-table" data-cy="rfr-visits-table">
    <CancelVisitFormModal
      v-if="showCancelVisitModal"
      :visitId="cancelVisitId"
      :hideVisitId="cancelHideVisitId"
      @closeModal="closeCancelVisitModal"
    />
    <div class="grid-control__container" data-cy="header-container">
      <div class="rfr-visits-table__actions">
        <LoadingButton
          themeColor="primary"
          type="button"
          :loading="exporting"
          @click="exportAllExcelAsync(true)"
          >Export Filtered Data To Excel
        </LoadingButton>

        <LoadingButton
          themeColor="primary"
          type="button"
          :loading="exporting"
          @click="exportAllExcelAsync(false)"
          >Export All To Excel
        </LoadingButton>
        <DateRangePicker
          v-if="
            mode == EnumVisitMode.Historical || mode == EnumVisitMode.Payable
          "
          class="grid-control__container-date-picker"
          :value="dateRange"
          :min="minStartDate"
          @change="changeDate"
          data-cy="date-range-picker"
        />
        <Button
          v-if="
            mode == EnumVisitMode.Historical || mode == EnumVisitMode.Payable
          "
          :theme-color="'primary'"
          type="button"
          @click="filterByDate"
          data-cy="date-button"
          >Find My Visits</Button
        >
        <ToggleSwitch
          class="grid-control__container-toggle"
          v-if="isGroupLeader && isRc"
          v-model="includeGroupMembersVisits"
          text="Show Group Members' Visits"
        />
        <ToggleSwitch
          class="grid-control__container-toggle"
          v-if="isRc && mode == EnumVisitMode.Inspection"
          v-model="includeRcOpenVisits"
          text="Show Open Visits"
        />
      </div>
      <div class="pull-right">
        <Button
          v-if="allowCloseVisits"
          theme-color="success"
          type="button"
          @click="closeVisitsAsync"
          data-cy="date-button"
          >Close Checked Visits</Button
        >
        <ClearFiltersButton @clearFiltersClicked="clearFilters" />
      </div>
    </div>
    <RfrVisitsGrid
      :filter="filter"
      :visits="visits"
      :mode="mode"
      :menuType="menuType"
      :loading="visitsLoading"
      :skip="skip"
      :pageSize="take"
      :total="totalVisits"
      :sort="sortDescriptors"
      ref="gridRef"
      :isTeamLeader="isTeamLeader"
      :getPropValues="getPropValuesAsync"
      @visitsChanged="onVisitsChanged"
      @sortChanged="onSortChanged"
      @cancelVisitClicked="onCancelVisitClicked"
    />
    <ColumnMenuIconHandler :filter="filter" />
  </div>
</template>

<script setup lang="ts">
import ClearFiltersButton from "@/shared/ui/inputs/ClearFiltersButton.vue";
import { Button } from "@progress/kendo-vue-buttons";
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { computed, onBeforeMount, ref, watch } from "vue";
import type {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import { CacheService } from "@/shared/services/CacheService";
import { SessionCacheService } from "@/shared/services/SessionCacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import type { GridFilterChangeEvent } from "@progress/kendo-vue-grid";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import {
  getJsonFilterString,
  FilterValuesTake,
  valueFilterConvert,
  dateConvert,
  dateConvertBack,
} from "@/shared/helpers/filtersHelper";
import type { ItemSortChangedOptions } from "@/Domain/ItemChangedOptions";
import ColumnMenuIconHandler from "@/shared/ui/filters/ColumnMenuIconHandler.vue";
import type {
  GetEirVisitsForRfrWithFilteringRequest,
  GetVisitsForRcWithFilteringRequest,
  VisitDisplayExtended,
} from "@/api/rfrVisitsSearch";
import type { SelectionRange } from "@progress/kendo-vue-dateinputs";
import RfrVisitsGrid from "./RfrVisitsGrid.vue";
import { saveExcel } from "@progress/kendo-vue-excel-export";
import moment from "moment";
import { EnumVisitMode } from "@/Domain/EnumVisitMode";
import CancelVisitFormModal from "./CancelVisitFormModal.vue";
import { EnumVisitType } from "@/Domain/EnumVisitType";
import type { ValueFieldConvertOption } from "@/Domain/ValueFieldConvertOption";
import enumHelper from "@/shared/helpers/enumHelper";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import DateRangePicker from "@/shared/ui/inputs/DateRangePicker.vue";
import { setDateColumnsFormat } from "@/shared/helpers/exportHelper";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import { UserRoles } from "@/Domain/UserRoles";
import ToggleSwitch from "@/shared/ui/switch/ToggleSwitch.vue";
import { useQueryParams } from "@/shared/helpers/useQueryParams";

const main = useMainStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  mode: EnumVisitMode;
  menuType: string;
  allowCloseVisits?: boolean;
  tripVisits: number[];
}>();

const { getParam, setParam } = useQueryParams();

const visits = ref<VisitDisplayExtended[]>([]);
const totalVisits = ref<number>(0);
const visitsLoading = ref<boolean>(false);
const skip = ref<number>(0);
const take = ref<number>(200);
const searchInput = ref<string>("");
const sortField = ref<string>("");
const sortAscending = ref<boolean>(false);
const isTeamLeader = ref<boolean>(false);

const isGroupLeader = computed(() =>
  main.isInRole(UserRoles.ROLE_GROUP_LEADER)
);
const isRc = computed(() => props.menuType == "VisitsAsRc");

const includeGroupMembersVisitsCacheKey = computed(
  () => "RfrVisitGlMode_" + main.userLogin
);
const includeOpenVisitsCacheKey = computed(
  () => "RfrVisitIncludeOpenVisits_" + main.userLogin
);

if (
  isGroupLeader.value &&
  SessionCacheService.has(includeGroupMembersVisitsCacheKey.value)
) {
  setParam(
    "glmode",
    SessionCacheService.get(includeGroupMembersVisitsCacheKey.value) ? "all" : "my"
  );
}

if (
  isRc.value &&
  props.mode == EnumVisitMode.Inspection &&
  SessionCacheService.has(includeOpenVisitsCacheKey.value)
) {
  setParam(
    "openVisits",
    SessionCacheService.get(includeOpenVisitsCacheKey.value) ? "show" : "hide"
  );
}

const includeRcOpenVisits = ref(
  isRc.value && props.mode == EnumVisitMode.Inspection
    ? getParam("openVisits")?.toLowerCase() == "show"
    : false
);

if (
  isGroupLeader.value &&
  SessionCacheService.has(includeGroupMembersVisitsCacheKey.value)
) {
  setParam(
    "glmode",
    SessionCacheService.get(includeGroupMembersVisitsCacheKey.value) ? "all" : "my"
  );
}

const includeGroupMembersVisits = ref(
  isGroupLeader.value ? getParam("glmode")?.toLowerCase() == "all" : true
);

const tableName = "RfrVisits" + props.mode;

if (isRc.value) {
  sortField.value = "StatusDate";
} else if (props.mode === EnumVisitMode.Historical) {
  sortField.value = "VisitDate";
} else if (props.mode === EnumVisitMode.Inspection) {
  sortField.value = "PlantName";
  sortAscending.value = true;
} else if (props.mode === EnumVisitMode.Financial) {
  sortField.value = "VisitDate";
  sortAscending.value = true;
} else if (props.mode === EnumVisitMode.Payable) {
  sortField.value = "TripId";
}

const exporting = ref(false);
const gridRef = ref<InstanceType<typeof RfrVisitsGrid>>();
const showCancelVisitModal = ref(false);
const cancelVisitId = ref<number>(0);
const cancelHideVisitId = ref(false);

const currentDate = new Date();
const startDate =
  props.mode === EnumVisitMode.Financial
    ? moment().subtract(3, "months").startOf("day").toDate()
    : moment().subtract(1, "years").startOf("day").toDate();

// limit EFR date range to 3 months before today
const minStartDate =
  props.mode === EnumVisitMode.Financial
    ? moment().subtract(3, "months").startOf("day").toDate()
    : undefined;

const dateRange = ref({
  start: new Date(startDate),
  end: new Date(currentDate),
} as SelectionRange);

const filter = ref<CompositeFilterDescriptor>(
  GridOptionsService.loadGridFilter(tableName)
);

const valueFiltersConvertOptions = [
  {
    column: "Type",
    convert: (value: number) => {
      return enumHelper.splitByCapitalLetter(EnumVisitType[value]);
    },
    convertBack: (value: string) => {
      const key = enumHelper.joinByCapitalLetter(EnumVisitType, value);
      return EnumVisitType[key as keyof typeof EnumVisitType];
    },
  },
  {
    column: "PeriodStartDate",
    convert: dateConvert,
    convertBack: dateConvertBack,
  },
  {
    column: "PeriodEndDate",
    convert: dateConvert,
    convertBack: dateConvertBack,
  },
  {
    column: "VisitDate",
    convert: dateConvert,
    convertBack: dateConvertBack,
  },
  {
    column: "PaymentProcessed",
    convert: dateConvert,
    convertBack: dateConvertBack,
  },
  {
    column: "StatusDate",
    convert: dateConvert,
    convertBack: dateConvertBack,
  },
  {
    column: "LastUpdateDate",
    convert: dateConvert,
    convertBack: dateConvertBack,
  },
] as ValueFieldConvertOption[];

const sortDescriptors = computed(() => {
  return (
    sortField.value
      ? [
          {
            field: sortField.value,
            dir: sortAscending.value ? "asc" : "desc",
          },
        ]
      : []
  ) as SortDescriptor[];
});

const getEirVisitsForRfrWithFiltering = async function (
  filterObject: CompositeFilterDescriptor | null = filter.value,
  takeValue: number = take.value,
  skipValue: number = skip.value,
  valuesForFieldOnly: string = ""
) {
  const requestParams = {
    rfrLogin: main.userLogin,
    visitMode: props.mode,
    beginDate: dateRange.value.start?.toLocaleDateString("en-US"),
    endDate: dateRange.value.end?.toLocaleDateString("en-US"),
    filters: getJsonFilterString(filterObject, valueFiltersConvertOptions),
    take: takeValue,
    skip: skipValue,
    sortField: sortField.value,
    ascending: sortAscending.value,
    plantId: 0,
    getOtherRfrsVisits: false,
  } as GetEirVisitsForRfrWithFilteringRequest;

  if (!valuesForFieldOnly)
    return api.get("GetEirVisitsForRfrWithFiltering", requestParams);

  requestParams.valuesForFieldOnly = valuesForFieldOnly;
  requestParams.take = FilterValuesTake;
  requestParams.skip = 0;

  return CacheService.getResponseAsync(
    createCacheKey(requestParams, "GetEirVisitsForRfrWithFiltering"),
    () => api.get("GetEirVisitsForRfrWithFiltering", requestParams)
  );
};

const getVisitsForRcWithFilteringAsync = async function (
  filterObject: CompositeFilterDescriptor | null = filter.value,
  takeValue: number = take.value,
  skipValue: number = skip.value,
  valuesForFieldOnly?: string
) {
  const requestParams = {
    userLogin: main.userLogin,
    visitMode: props.mode,
    beginDate: dateRange.value.start?.toLocaleDateString("en-US"),
    endDate: dateRange.value.end?.toLocaleDateString("en-US"),
    filters: getJsonFilterString(filterObject, valueFiltersConvertOptions),
    take: takeValue,
    skip: skipValue,
    sortField: sortField.value,
    ascending: sortAscending.value,
    includeGroupMembersVisits: includeGroupMembersVisits.value,
    includeOpenVisits: includeRcOpenVisits.value,
  } as GetVisitsForRcWithFilteringRequest;

  if (!valuesForFieldOnly)
    return api.get("GetVisitsForRcWithFiltering", requestParams);

  requestParams.valuesForFieldOnly = valuesForFieldOnly;
  requestParams.take = FilterValuesTake;
  requestParams.skip = 0;

  return CacheService.getResponseAsync(
    createCacheKey(requestParams, "GetVisitsForRcWithFiltering"),
    () => api.get("GetVisitsForRcWithFiltering", requestParams)
  );
};

const getVisitsAsync = async function () {
  if (visitsLoading.value) return;
  visitsLoading.value = true;

  await handleResponsePromiseAsync(
    isRc.value
      ? getVisitsForRcWithFilteringAsync()
      : getEirVisitsForRfrWithFiltering(),
    (data) => {
      visits.value = data.ListItems;
      totalVisits.value = data.TotalItemsInList;
      if (props.mode == EnumVisitMode.Financial) updateVisitsSelection();
    },
    "RfrVisitsTable.vue",
    "getVisitsAsync"
  );

  visitsLoading.value = false;
};

const updateVisitsSelection = function () {
  visits.value.forEach((v) => {
    v.selected = props.tripVisits.some((tv) => tv == v.Id);
  });
};

const updateIsTeamLeaderValueAsync = async function () {
  await handleResponsePromiseAsync(
    api.get("IsRfrTeamLeader", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
    }),
    (data) => {
      isTeamLeader.value = data;
    },
    "RfrVisitsTable.vue",
    "updateIsTeamLeaderValueAsync"
  );
};

const getPropValuesAsync = async function (column: GridColumnPropsExtended) {
  let result: VisitDisplayExtended[] = [];
  await handleResponsePromiseAsync(
    isRc.value
      ? getVisitsForRcWithFilteringAsync(
          filter.value,
          take.value,
          skip.value,
          column.field
        )
      : getEirVisitsForRfrWithFiltering(
          filter.value,
          take.value,
          skip.value,
          column.field
        ),
    (data) => {
      result = data.ListItems;
      valueFilterConvert(valueFiltersConvertOptions, column.field!, result);
    },
    "RfrVisitsTable.vue",
    "getPropValuesAsync"
  );
  return result;
};

const closeVisitsAsync = async function () {
  const visitIds = visits.value.filter((v) => v.selected).map((v) => v.Id);

  if (visitIds.length === 0) {
    notificationStore.notify({
      message: "No visits selected to close.",
      type: NotificationTypeEnum.info,
      duration: 5000,
    });
    return;
  }

  await handleResponsePromiseAsync(
    api.post("CloseVisits", {
      VisitIds: visitIds,
    }),
    () => {
      getVisitsAsync();
      notificationStore.notify({
        message: "Visits closed successfully.",
        type: NotificationTypeEnum.success,
        duration: 5000,
      });
    },
    "RfrVisitsTable.vue",
    "closeVisitsAsync",
    () => {
      notificationStore.notify({
        message: "Failed to close visits. Please try again.",
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
    }
  );
};

const onVisitsChanged = function (
  skipValue: number,
  takeValue: number,
  filterEvent: GridFilterChangeEvent | null
) {
  if (filterEvent) {
    filter.value.filters = filterEvent.filter.filters;
    GridOptionsService.saveGridFilter(tableName, filter.value);
  }
  skip.value = skipValue;
  if (take.value != takeValue) {
    take.value = takeValue;
    GridOptionsService.savePageSizeAsync(tableName, takeValue);
  }
  getVisitsAsync();
};

const changeDate = function (newRange: SelectionRange) {
  dateRange.value = newRange;
};

const filterByDate = function () {
  skip.value = 0;
  getVisitsAsync();
};

const onSortChanged = function (options: ItemSortChangedOptions) {
  sortField.value = options.field;
  sortAscending.value = options.ascending;
  skip.value = 0;
  getVisitsAsync();
};

const onCancelVisitClicked = function (visit: VisitDisplayExtended) {
  cancelVisitId.value = visit.Id;
  cancelHideVisitId.value = visit.RfrCreatedVisit;
  showCancelVisitModal.value = true;
};

const closeCancelVisitModal = function (canceled: boolean) {
  showCancelVisitModal.value = false;
  if (canceled) getVisitsAsync();
};

const clearFilters = function () {
  console.debug("RfrVisitsTable.vue, clearFilters()");
  filter.value = {
    logic: "and",
    filters: [{} as FilterDescriptor],
  };

  GridOptionsService.saveGridFilter(tableName, filter.value);

  searchInput.value = "";
  sortField.value = "";
  sortAscending.value = false;
  getVisitsAsync();
};

const exportFormatData = function (
  data: VisitDisplayExtended[],
  columns: GridColumnPropsExtended[] | undefined
) {
  const typeColumn = columns?.find((c) => c.field == "Type");
  if (typeColumn) typeColumn.field = "typeString";

  // Exclude visit id for RFR created visits
  for (const visit of data) {
    if (visit.RfrCreatedVisit && props.mode == EnumVisitMode.Inspection)
      visit.Id = 0;
    visit.typeString = enumHelper.splitByCapitalLetter(
      EnumVisitType[visit.Type]
    );
  }
  return data;
};

const exportAllExcelAsync = async function (filtered: boolean = false) {
  try {
    exporting.value = true;
    const data = await (isRc.value
      ? getVisitsForRcWithFilteringAsync(
          filtered ? filter.value : null,
          999999,
          0,
          ""
        )
      : getEirVisitsForRfrWithFiltering(
          filtered ? filter.value : null,
          999999,
          0,
          ""
        ));

    if (!data.success) {
      notificationStore.notify({
        message: "Error while exporting data. Please try again.",
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
      return;
    }
    const columns = gridRef.value?.getColumns();

    setDateColumnsFormat(columns, data.response.ListItems);

    saveExcel({
      data: exportFormatData(data.response.ListItems, columns),
      fileName: "Visits.xlsx",
      columns: columns,
    });
  } finally {
    exporting.value = false;
  }
};

defineExpose({
  visits,
});

watch(
  () => props.tripVisits,
  () => {
    if (props.mode == EnumVisitMode.Financial) updateVisitsSelection();
  }
);

watch(
  () => [includeRcOpenVisits.value, includeGroupMembersVisits.value],
  () => {
    skip.value = 0;
    if (isGroupLeader.value) {
      setParam("glmode", includeGroupMembersVisits.value ? "all" : "my");
      SessionCacheService.set(
        includeGroupMembersVisitsCacheKey.value,
        includeGroupMembersVisits.value
      );
    }
    if (isRc.value && props.mode == EnumVisitMode.Inspection) {
      setParam("openVisits", includeRcOpenVisits.value ? "show" : "hide");
      SessionCacheService.set(
        includeOpenVisitsCacheKey.value,
        includeRcOpenVisits.value
      );
    }
    getVisitsAsync();
  }
);

onBeforeMount(async () => {
  const pageSize = await GridOptionsService.getPageSizeAsync(tableName);
  if (pageSize) take.value = pageSize;
  await getVisitsAsync();
  await updateIsTeamLeaderValueAsync();
});
</script>

<style lang="scss">
.rfr-visits-table {
  .grid-control__container {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 12px;

    &-date-picker {
      width: auto;
    }
  }

  &__actions {
    display: flex;
    gap: 8px;
    align-items: flex-end;
    flex-wrap: wrap;
  }

  .pull-right {
    display: flex;
    margin-left: auto;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .grid-control__container-toggle {
    height: 32px;
  }
}
</style>
